import {
    Box,
    Button,
    Chip,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import {DataGrid} from "@mui/x-data-grid"
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded"
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded"
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded"
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded"
import React, {useCallback, useEffect, useMemo, useState} from "react"
import dayjs from "dayjs"
import AlertMessage from "src/components/alert-message"
import LayoutPage from "src/components/page-layout/layout-page"
import {CurvasCreditoWorkflowService} from "src/services/curvas-credito-workflow-service"
import {localeBRDataGridText} from "src/utils/data-grid-local-text"


const WORKFLOW_OPTIONS = {
    corp: {
        label: "CORP",
        verticesLabel: "Selecionar vértices",
        resumoLabel: "Selecionar resumo",
    },
    if: {
        label: "IF",
        verticesLabel: "Selecionar vértices",
        resumoLabel: "Selecionar resumo",
    },
}

const STATUS_VIEW = {
    PENDING: {label: "PENDENTE", color: "warning", progress: 5},
    SETUP_TASK: {label: "SETUP", color: "info", progress: 15},
    READY_TO_CALCULATE: {label: "PRONTO", color: "info", progress: 30},
    CALCULATING: {label: "CALCULANDO", color: "primary", progress: 65},
    REPORT_IN_PROGRESS: {label: "GERANDO SAÍDA", color: "primary", progress: 85},
    REPORT_DONE: {label: "CONCLUÍDO", color: "success", progress: 100},
    ERROR: {label: "ERRO", color: "error", progress: 100},
}

const getStatusView = (status) => (
    STATUS_VIEW[status] || {label: status || "NÃO INFORMADO", color: "default", progress: 0}
)

const formatDateTime = (value) => {
    if (!value) {
        return "-"
    }

    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : value
}

const formatDate = (value) => {
    if (!value) {
        return "-"
    }

    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : value
}

const formatDuration = (seconds) => {
    if (!seconds) {
        return "-"
    }

    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.round(seconds % 60)
    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`
}

const CurvasCreditoWorkflowsPage = () => {
    const workflowService = useMemo(() => new CurvasCreditoWorkflowService(), [])
    const [workflowType, setWorkflowType] = useState("corp")
    const [dataInicial, setDataInicial] = useState(dayjs().subtract(30, "day").format("YYYY-MM-DD"))
    const [dataFinal, setDataFinal] = useState(dayjs().format("YYYY-MM-DD"))
    const [verticesFile, setVerticesFile] = useState(null)
    const [resumoFile, setResumoFile] = useState(null)
    const [useGpu, setUseGpu] = useState(true)
    const [runs, setRuns] = useState([])
    const [loading, setLoading] = useState(false)
    const [tableLoading, setTableLoading] = useState(false)
    const [alertMessageState, setAlertMessageState] = useState({
        open: false,
        message: "",
        type: "info",
    })

    const currentWorkflow = WORKFLOW_OPTIONS[workflowType]

    const openAlert = useCallback((message, type) => {
        setAlertMessageState({open: true, message, type})
    }, [])

    const closeAlert = useCallback(() => {
        setAlertMessageState((state) => ({...state, open: false}))
    }, [])

    const loadRuns = useCallback(async () => {
        setTableLoading(true)
        try {
            const response = await workflowService.listRuns({limit: 200})
            const results = response?.body?.results || []
            setRuns(results.map((run) => ({...run, id: run.id || run.external_workflow_id})))
        } catch (error) {
            console.error("Erro ao carregar workflows de Curvas Crédito", error)
            openAlert("Erro ao carregar execuções", "error")
        } finally {
            setTableLoading(false)
        }
    }, [openAlert, workflowService])

    useEffect(() => {
        loadRuns()
        const interval = setInterval(loadRuns, 30000)
        return () => clearInterval(interval)
    }, [loadRuns])

    const handleWorkflowChange = (_event, value) => {
        setWorkflowType(value)
        setVerticesFile(null)
        setResumoFile(null)
    }

    const handleSubmit = async () => {
        if (!verticesFile || !resumoFile) {
            openAlert("Selecione os arquivos de entrada", "warning")
            return
        }

        setLoading(true)
        try {
            await workflowService.startWorkflow({
                workflowType,
                dataInicial,
                dataFinal,
                verticesFile,
                resumoFile,
                useGpu,
            })
            openAlert("Workflow iniciado", "success")
            setVerticesFile(null)
            setResumoFile(null)
            await loadRuns()
        } catch (error) {
            const message = error?.response?.text || error?.message || "Erro ao iniciar workflow"
            openAlert(message, "error")
        } finally {
            setLoading(false)
        }
    }

    const handleDownload = useCallback(async (downloadEndpoint) => {
        setLoading(true)
        try {
            const response = await workflowService.getDownload(downloadEndpoint)
            const url = response?.body?.download_url
            if (url) {
                window.open(url, "_blank", "noopener,noreferrer")
            }
        } catch (error) {
            openAlert("Arquivo ainda não disponível", "warning")
        } finally {
            setLoading(false)
        }
    }, [workflowService])

    const columns = useMemo(() => [
        {
            field: "workflow_label",
            headerName: "Workflow",
            minWidth: 90,
            flex: 0.4,
            renderCell: (params) => <Typography variant="body2" fontWeight={700}>{params.value}</Typography>,
        },
        {
            field: "status",
            headerName: "Status",
            minWidth: 170,
            flex: 0.8,
            renderCell: (params) => {
                const statusView = getStatusView(params.value)
                return (
                    <Stack spacing={0.7} sx={{width: "100%"}}>
                        <Chip
                            label={statusView.label}
                            color={statusView.color}
                            size="small"
                            sx={{width: "fit-content", fontWeight: 700}}
                        />
                        <LinearProgress
                            variant="determinate"
                            value={statusView.progress}
                            color={statusView.color === "default" ? "primary" : statusView.color}
                            sx={{height: 6, borderRadius: 1, width: "100%"}}
                        />
                    </Stack>
                )
            },
        },
        {
            field: "periodo",
            headerName: "Período",
            minWidth: 180,
            flex: 0.7,
            valueGetter: (params) => `${formatDate(params.row.data_inicial)} a ${formatDate(params.row.data_final)}`,
        },
        {
            field: "criado_em",
            headerName: "Criado em",
            minWidth: 150,
            flex: 0.6,
            valueGetter: (params) => formatDateTime(params.value),
        },
        {
            field: "nome_usuario",
            headerName: "Usuário",
            minWidth: 150,
            flex: 0.7,
            valueGetter: (params) => params.value || params.row.criado_por || "-",
        },
        {
            field: "duration_seconds",
            headerName: "Duração",
            minWidth: 110,
            flex: 0.4,
            valueGetter: (params) => formatDuration(params.value),
        },
        {
            field: "outputs",
            headerName: "Saídas",
            minWidth: 210,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const isDone = params.row.status === "REPORT_DONE"
                const outputs = params.value || []
                return (
                    <Stack direction="row" spacing={0.5} sx={{overflow: "hidden"}}>
                        {outputs.map((output) => (
                            <Tooltip title={output.filename} key={output.filename}>
                                <span>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        disabled={!isDone}
                                        onClick={() => handleDownload(output.download_endpoint)}
                                    >
                                        <DownloadRoundedIcon fontSize="small"/>
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ))}
                    </Stack>
                )
            },
        },
    ], [handleDownload])

    return (
        <LayoutPage title="Tratamento Spreads" loading={loading}>
            <AlertMessage
                open={alertMessageState.open}
                message={alertMessageState.message}
                type={alertMessageState.type}
                close={closeAlert}
            />

            <Box sx={{display: "flex", flexDirection: "column", gap: 2, pt: 4, pb: 4}}>
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 2, flexWrap: "wrap"}}>
                    <Typography variant="h5" fontWeight={700}>Tratamento Spreads</Typography>
                </Box>

                <Paper sx={{p: 2, borderRadius: 1}}>
                    <Tabs value={workflowType} onChange={handleWorkflowChange} sx={{mb: 2}}>
                        <Tab label="Curvas CORP" value="corp"/>
                        <Tab label="Curvas IF" value="if"/>
                    </Tabs>

                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                label="Data inicial"
                                type="date"
                                value={dataInicial}
                                onChange={(event) => setDataInicial(event.target.value)}
                                InputLabelProps={{shrink: true}}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                label="Data final"
                                type="date"
                                value={dataFinal}
                                onChange={(event) => setDataFinal(event.target.value)}
                                InputLabelProps={{shrink: true}}
                            />
                        </Grid>
                        <Grid item xs={12} md={workflowType === "if" ? 2.5 : 3}>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">Arquivo de vértices</Typography>
                                <Button
                                    fullWidth
                                    variant={verticesFile ? "contained" : "outlined"}
                                    component="label"
                                    startIcon={<CloudUploadRoundedIcon/>}
                                    sx={{
                                        minHeight: 44,
                                        justifyContent: "flex-start",
                                        textTransform: "none",
                                        "& .MuiButton-startIcon": {flexShrink: 0},
                                    }}
                                >
                                    <Box component="span" sx={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                        {verticesFile ? verticesFile.name : currentWorkflow.verticesLabel}
                                    </Box>
                                    <input
                                        hidden
                                        type="file"
                                        accept=".xlsx"
                                        onChange={(event) => setVerticesFile(event.target.files?.[0] || null)}
                                    />
                                </Button>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={workflowType === "if" ? 2.5 : 3}>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">Arquivo de resumo</Typography>
                                <Button
                                    fullWidth
                                    variant={resumoFile ? "contained" : "outlined"}
                                    component="label"
                                    startIcon={<CloudUploadRoundedIcon/>}
                                    sx={{
                                        minHeight: 44,
                                        justifyContent: "flex-start",
                                        textTransform: "none",
                                        "& .MuiButton-startIcon": {flexShrink: 0},
                                    }}
                                >
                                    <Box component="span" sx={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                        {resumoFile ? resumoFile.name : currentWorkflow.resumoLabel}
                                    </Box>
                                    <input
                                        hidden
                                        type="file"
                                        accept=".xlsx"
                                        onChange={(event) => setResumoFile(event.target.files?.[0] || null)}
                                    />
                                </Button>
                            </Stack>
                        </Grid>
                        {workflowType === "if" && (
                            <Grid item xs={12} md={1}>
                                <FormControl fullWidth>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={useGpu}
                                                onChange={(event) => setUseGpu(event.target.checked)}
                                            />
                                        }
                                        label="GPU"
                                    />
                                </FormControl>
                            </Grid>
                        )}
                        <Grid item xs={12} md={2} sx={{display: "flex", justifyContent: "flex-end"}}>
                            <Button
                                variant="contained"
                                startIcon={<PlayArrowRoundedIcon/>}
                                onClick={handleSubmit}
                                disabled={loading}
                                sx={{minHeight: 44, minWidth: {xs: "100%", md: 160}}}
                            >
                                Iniciar
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Grid item>
                        <Typography variant="h5">Últimas Execuções</Typography>
                    </Grid>
                    <Grid item>
                        <Tooltip title="Atualizar execuções">
                            <Button
                                variant="outlined"
                                onClick={loadRuns}
                                disabled={tableLoading}
                                aria-label="Atualizar execuções"
                                sx={{minWidth: 44, height: 36.5, px: 1.2}}
                            >
                                <RefreshRoundedIcon/>
                            </Button>
                        </Tooltip>
                    </Grid>
                </Grid>

                <Paper sx={{height: 560, p: 2, borderRadius: 1}}>
                    <DataGrid
                        rows={runs}
                        columns={columns}
                        loading={tableLoading}
                        localeText={localeBRDataGridText}
                        disableRowSelectionOnClick
                        initialState={{
                            pagination: {
                                paginationModel: {pageSize: 25},
                            },
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        getRowHeight={() => 64}
                        sx={{
                            border: "none",
                            "& .MuiDataGrid-cell": {
                                alignItems: "center",
                            },
                        }}
                    />
                </Paper>
            </Box>
        </LayoutPage>
    )
}

export default CurvasCreditoWorkflowsPage
